// src/pages/UserManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Flex,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
  Center,
  Select
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';

import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/Dashboard/Sidebar';
import { getAllUsers, updateUserPassword, deleteUser } from '../../api/userService';

function UserManagement() {
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if current user is admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to access this page.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // You might want to redirect here
      return;
    }
  }, [user, toast]);

  // Fetch all users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const userData = await getAllUsers();
      setUsers(userData);
    } catch (error) {
      toast({
        title: 'Error Loading Users',
        description: error.response?.data?.detail || 'Failed to load users.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Determine badge color based on user role
  const getBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'expert':
        return 'purple';
      default:
        return 'green';
    }
  };

  // Handle edit user password
  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setPasswordForm({
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
    onEditOpen();
  };

  // Handle delete user
  const handleDeleteUser = (userToDelete) => {
    setSelectedUser(userToDelete);
    onDeleteOpen();
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate password form
  const validateForm = () => {
    const newErrors = {};

    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters';
    }

    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm the new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle password update submission
  const handlePasswordUpdate = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await updateUserPassword(selectedUser.user_id, passwordForm.newPassword);
      
      toast({
        title: 'Password Updated',
        description: `Password for ${selectedUser.username} has been updated successfully.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onEditClose();
      
    } catch (error) {
      toast({
        title: 'Password Update Failed',
        description: error.response?.data?.detail || 'An error occurred while updating password.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle user deletion
  const handleDeleteConfirm = async () => {
    setIsSubmitting(true);
    
    try {
      await deleteUser(selectedUser.user_id);
      
      toast({
        title: 'User Deleted',
        description: `User ${selectedUser.username} has been deleted successfully.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Refresh users list
      fetchUsers();
      onDeleteClose();
      
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || 'An error occurred while deleting user.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleEditClose = () => {
    setPasswordForm({
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
    setSelectedUser(null);
    onEditClose();
  };

  const handleDeleteClose = () => {
    setSelectedUser(null);
    onDeleteClose();
  };

  if (!user || user.role !== 'admin') {
    return (
      <Flex direction="row" h="100vh">
        <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
        <Container maxW="container.xl" py={8}>
          <Center>
            <VStack spacing={4}>
              <Heading size="lg" color="red.500">Access Denied</Heading>
              <Text>You do not have permission to access this page.</Text>
            </VStack>
          </Center>
        </Container>
      </Flex>
    );
  }

  return (
    <Flex direction="row" h="100vh">
      {/* Collapsible Sidebar Component */}
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
      <Container maxW="container.xl" py={8} pl={sidebarCollapsed ? "60px" : "250px"} overflowY="auto">
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading size="xl">User Management</Heading>
            <Button colorScheme="blue" onClick={fetchUsers} isLoading={loading}>
              Refresh
            </Button>
          </HStack>

          <Box p={5}  shadow="md" borderWidth="1px" borderRadius="md">
            {loading ? (
              <Center py={10}>
                <Spinner size="xl" />
              </Center>
            ) : (
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Username</Th>
                      <Th>Email</Th>
                      <Th>Role</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {users.map((userData) => (
                      <Tr key={userData.user_id}>
                        <Td fontWeight="medium">{userData.username}</Td>
                        <Td>{userData.email || 'N/A'}</Td>
                        <Td>
                          <Badge colorScheme={getBadgeColor(userData.role)}>
                            {userData.role}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={userData.disabled ? 'red' : 'green'}>
                            {userData.disabled ? 'Disabled' : 'Active'}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Edit user password"
                              icon={<EditIcon />}
                              size="sm"
                              colorScheme="blue"
                              onClick={() => handleEditUser(userData)}
                              isDisabled={userData.user_id === user.userId || userData.role == 'admin'} // Prevent editing own account
                            />
                            <IconButton
                              aria-label="Delete user"
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              onClick={() => handleDeleteUser(userData)}
                              isDisabled={userData.user_id === user.userId || userData.role == 'admin'} // Prevent deleting own account
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}

            {!loading && users.length === 0 && (
              <Center py={10}>
                <Text color="gray.500">No users found.</Text>
              </Center>
            )}
          </Box>
        </VStack>
      </Container>

      {/* Edit Password Modal */}
      <Modal isOpen={isEditOpen} onClose={handleEditClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Change Password for {selectedUser?.username}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.newPassword}>
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                />
                <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.confirmPassword}>
                <FormLabel>Confirm New Password</FormLabel>
                <Input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm new password"
                />
                <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleEditClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handlePasswordUpdate}
              isLoading={isSubmitting}
              loadingText="Updating..."
            >
              Update Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        onClose={handleDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete User
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete user <strong>{selectedUser?.username}</strong>? 
              This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={handleDeleteClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDeleteConfirm}
                ml={3}
                isLoading={isSubmitting}
                loadingText="Deleting..."
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
}

export default UserManagement;